import { useGameAudioStore } from '@/stores/gameAudioStore';

// import { PromiseSettledResult, PromiseRejectedResult } from 'typescript';

interface GamePlayStore {
  assets: {
    images: Record<string, string>;
    audio: Record<string, string>;
  };
}

export default class MediaTools {
  private gamePlayStore: GamePlayStore;
  private setProgress: (progress: number) => void;

  constructor(
    gamePlayStore: GamePlayStore,
    setProgress: (progress: number) => void
  ) {
    this.gamePlayStore = gamePlayStore;
    this.setProgress = setProgress;
  }

  /**
   * Helper function to load an audio using the game audio store
   * @param src - The source URL of the audio.
   */
  async loadAudio(src: string): Promise<void> {
    const gameAudio = useGameAudioStore();
    await gameAudio.preloadAudioFiles([src]);
  }

  /**
   * Helper function to load an image and return a promise
   * @param src - The source URL of the image.
   */
  loadImage(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve();
      };
      img.onerror = (error) => {
        reject(error);
      };
      img.src = src;
    });
  }

  getReasonMessage(reason: unknown): string {
    if (reason instanceof Error) {
      return reason.message;
    } else if (typeof reason === 'string') {
      return reason;
    } else {
      return 'Unknown error';
    }
  }

  /**
   * Preloads assets by looping through the assets object and loading them into the browser cache
   * Optionally reports which phase is currently loading (images, audio, done)
   */
  async preloadAssets(
    onPhaseChange?: (phase: 'images' | 'audio' | 'done') => void
  ): Promise<void> {
    const flattenedImages = this.flattenObject(
      this.gamePlayStore.assets.images
    );
    const flattenedAudio = this.flattenObject(this.gamePlayStore.assets.audio);
    const images = Object.values(flattenedImages);
    const audio = Object.values(flattenedAudio);

    // Map raw audio URLs into AudioFileDefinition objects expected by gameAudioStore
    const audioFiles = audio.map((url, index) => ({
      key: `preload_${index}`,
      url,
    }));

    const total = images.length + audioFiles.length;
    this.setProgress(0);

    // Initialize game audio
    const gameAudio = useGameAudioStore();

    // ---- IMAGE PHASE ----
    onPhaseChange?.('images');

    const imagePromises: Promise<void>[] = images.map((src) =>
      this.loadImage(src)
    );
    const imageResults: PromiseSettledResult<void>[] =
      await Promise.allSettled(imagePromises);

    // ---- AUDIO PHASE ----
    onPhaseChange?.('audio');

    // Preload audio buffers; this method does not currently report per-file results
    await gameAudio.initializeAudioBuffers(audioFiles);

    // Calculate progress
    const successfulImages = imageResults.filter(
      (result) => result.status === 'fulfilled'
    ).length;
    const successfulAudio = audioFiles.length; // assume success; loader logs its own errors
    const totalSuccessful = successfulImages + successfulAudio;

    const progress =
      total > 0 ? Math.round((totalSuccessful / total) * 100) : 100;
    this.setProgress(progress);

    if (totalSuccessful === total) {
      console.log('All assets loaded successfully');
    } else {
      console.info(
        `${totalSuccessful} out of ${total} assets loaded successfully (continuing with available assets)`
      );
    }

    // ---- DONE PHASE ----
    onPhaseChange?.('done');
  }

  /**
   * Set the ticket increase sound effect
   */
  setTicketEffect(): void {
    const gameAudio = useGameAudioStore();
    gameAudio.playEffect('/ticket-increase.mp3');
  }

  private flattenObject(obj: Record<string, any>): Record<string, string> {
    const flattened: Record<string, string> = {};
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        Object.assign(flattened, this.flattenObject(obj[key]));
      } else {
        flattened[key] = obj[key];
      }
    });
    return flattened;
  }
}
